import { FileText, LifeBuoy, QrCode } from "lucide-react";

export const COMPARISON_LINKS = [
  {
    name: "Rebolt vs 180sites",
    slug: "comparison/rebolt-vs-180sites",
    image: "/competitors/180sites.webp",
  },
  {
    name: "Rebolt vs Hook Agency",
    slug: "comparison/rebolt-vs-hook-agency",
    image: "/competitors/hook-agency.webp",
  },
  {
    name: "Rebolt vs Scorpion",
    slug: "comparison/rebolt-vs-scorpion",
    image: "/competitors/scorpion.webp",
  },
  {
    name: "Rebolt vs Topline Pro",
    slug: "comparison/rebolt-vs-toplinepro",
    image: "/competitors/topline-pro.webp",
  },
  {
    name: "Rebolt vs Jobber",
    slug: "comparison/rebolt-vs-jobber",
    image: "/competitors/jobber.webp",
  },
];

export const ALTERNATIVES_LINKS = [
  {
    name: "180sites Alternatives",
    slug: "alternatives/180sites-alternatives",
    image: "/competitors/180sites.webp",
  },
  {
    name: "Hook Agency Alternatives",
    slug: "alternatives/hook-agency-alternatives",
    image: "/competitors/hook-agency.webp",
  },
  {
    name: "Scorpion Alternatives",
    slug: "alternatives/scorpion-alternatives",
    image: "/competitors/scorpion.webp",
  },
  {
    name: "Topline Pro Alternatives",
    slug: "alternatives/topline-pro-alternatives",
    image: "/competitors/topline-pro.webp",
  },
];

export const COMPANY = [
  {
    name: "Blog",
    slug: `${process.env.NEXT_PUBLIC_API_HOST}/resources`,
    icon: FileText,
  },
  {
    name: "Help Center",
    slug: "https://help.rebolthq.com",
    icon: LifeBuoy,
  },
] as const;

export const TOOLS = [
  {
    name: "Google Review QR Code Generator",
    slug: "google-reviews-qr-code-creator",
    icon: QrCode,
  },
  {
    name: "Google Business Profile QR Code Generator",
    slug: "gbp-qr-code-generator",
    icon: QrCode,
  },
] as const;

export const FAQ_ITEMS = [
  {
    question: "Are you an agency or a software company?",
    answer:
      "We are a software company that provides marketing services to home service businesses but with a twist: we use AI to make everything faster and more affordable.",
  },
  {
    question: "Will I be able to edit the site?",
    answer:
      "Yes, our easy to use website builder allows you to customize your site: including adding pages, changing colors, and more.",
  },
  {
    question: "Do I still own my domain?",
    answer: "Yes, you will own and have complete control over your domain. We don't hold it hostage!",
  },
  {
    question: "What happens to my old site?",
    answer:
      "We will point your domain to your new Rebolt site, anyone who visits your old site will be redirected to your brand new one.",
  },
  {
    question: "Will I ever own the site outright?",
    answer:
      "Because the website is intertwined into our platform, it makes it difficult for us to export the site and give it to you outright. You own all the content and imagery on the website.",
  },
  {
    question: "How long does it take to rank on google?",
    answer:
      "The time it takes to rank on google depends on your industry and competition, we've often seen improvements within the first 1 to 2 months, but sometimes as early as 1 week!",
  },
] as const;

export type Testimonial = {
  business?: string;
  author?: string;
  content: string;
  videoUrl?: string;
  thumbnailUrl?: string;
};

export const TESTIMONIALS: Testimonial[] = [
  {
    business: "Tek Overhead Door LLC",
    author: "Morgan",
    content:
      "The number of people that have brought up how good my website looks… they're like, oh my god, it's great… blown away by it. Has happened at least 10 times.",
  },
  {
    business: "Ress Outdoor Services, LLC",
    content:
      "Your site works so well… I want to help my buddies generate leads for their concrete business… because your site works so well and then giving them the work.",
  },
  {
    business: "Brechtech Services",
    content: "That looks so much more professional. Thank you so much! Holy cow, good job… you did it very fast.",
  },
  {
    business: "Priority Electrical",
    content: "Leads coming in regularly and truly value the work you're doing to make our lives simpler.",
  },
  {
    business: "Shields Lawn Care",
    content: "Never got leads organically from my website until you guys.",
  },
  {
    content:
      "Reason I went with you guys is I saw you had a mobile app and how you guys focus on automation and saving me time — review management.",
  },
  {
    business: "Lakeland Window Cleaning",
    content:
      "Never thought of my website being a source of leads but after two months with you guys I am actually getting calls and forms from my site.",
  },
  {
    business: "Evolution Concrete Pumping",
    content: "Whatever you guys are doing is working — we've had quite a few new customers that found us online.",
  },
  {
    author: "Kristin Toole",
    content:
      "Last week we had 3 customers that found us through AI… what you're doing is working definitely fantastic. My husband uploads the videos and they get posted right away… it made his job much easier.",
  },
  {
    author: "Nicolas Salinas",
    content:
      "I really like the fact that I take the pictures and post it real quick. I'm not so user friendly with social media stuff… but it's easy and smooth.",
  },
  {
    content:
      "I've been with Hibu for 3 years and never once did someone sit down and give us the attention and support that you have today.",
  },
  {
    business: "WattStar Electric",
    content: "The site looks awesome! Thanks for all of the hard work you guys are putting into this!",
  },
  {
    business: "Green Valley Wildlife Solutions LLC",
    content: "Best investment I've ever made was with you… everything is changing.",
  },
  {
    business: "Sunny Sweepers",
    author: "Reanna Veneracion",
    content:
      "Thank you for making the website so easy to build. Being able to build the website before paying was what sold me.",
  },
  {
    business: "Ted White Stone Cleaning Service",
    author: "Ted",
    content: "I love that you guys read our minds — you, or whoever did this, did a great job.",
  },
  {
    business: "1354 Landscaping",
    content:
      "This is awesome! I've found your website builder to be very simple to work with — and that's exactly what I wanted.",
  },
  {
    content: "You guys outdo yourself once again. The support through you guys is always unreal.",
  },
  {
    business: "Basin Outdoor Services",
    content: "I love this social media management software. So glad you have it.",
  },
  {
    business: "Patriot Pavers",
    author: "Brian Goody",
    content: "Amazing company.",
  },
  {
    business: "Maid Up Cleaners",
    content:
      "You didn't have to make a new wheel… you just kind of made the wheel better. I'm just super happy with your business model. You guys are doing a great job.",
  },
  {
    business: "Parker Landscape",
    content:
      "Just wanted to let you know the website worked. The website so far has introduced me two high quality leads. I am looking forward to many more.",
  },
  {
    business: "Razo Builders Inc",
    content:
      "I just got off the phone with Townsquare and canceled… once I mentioned the reviews. Thank you for saving me the headache.",
  },
  {
    business: "Kingdom Care Roofing and Construction LLC",
    content: "Perfect. Y'all have been very prompt, and it's much appreciated.",
  },
  {
    business: "Hercs Works",
    content: "Very impressed with you guys already. Website is 1000% better than anyone else we have tried.",
  },
  {
    business: "Packard Landscapes",
    author: "Austin",
    content:
      "Roughly $160k dollars worth of estimates went out for website based leads from January and February to date. This website is clearly working.",
  },
  {
    business: "Homepro",
    content:
      "OMG that looks incredibly amazing for my first impression on my phone! Your team knocked it out of the park. THANK YOU SO MUCH!",
  },
  {
    business: "Abe Doors",
    author: "Joe",
    thumbnailUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Thumbnails/Screenshot%202026-03-14%20at%203.43.36%E2%80%AFPM.png?updatedAt=1773548963606",
    videoUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/ABE%20Doors.mov/ik-video.mp4?updatedAt=1773548965401",
    content: "The process to post to social media and to the website through the app is a game changer.",
  },
  {
    business: "Douglas Carpet & Upholstery Cleaning",
    author: "Douglas",
    thumbnailUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Thumbnails/Screenshot%202026-03-14%20at%203.54.56%E2%80%AFPM.png?updatedAt=1773548963603",
    videoUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/IMG_6636.MOV/ik-video.mp4?updatedAt=1773548963727",
    content: "You can call them and belive me... they do a great job and work very hard.",
  },
  {
    business: "Bull City Crawl Space",
    author: "John C.",
    thumbnailUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Thumbnails/Screenshot%202026-03-14%20at%203.54.21%E2%80%AFPM.png?updatedAt=1773548963608",
    videoUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/bull%20city%20crawl%20space.mp4?updatedAt=1773548966040",
    content: "Before Rebolt I was plannning on shutting my business down and within 3 days I started getting leads.",
  },
  {
    business: "AP Exterior Services",
    author: "Alex",
    thumbnailUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Thumbnails/Screenshot%202026-03-14%20at%203.56.49%E2%80%AFPM.png?updatedAt=1773548963639",
    videoUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Washed%20by%20AP.MOV/ik-video.mp4?updatedAt=1773548969997",
    content: "Overall I would highly recomment Rebolt to any contractor that does great work.",
  },
  {
    business: "Poseidon Pressure Washing",
    author: "Alex",
    thumbnailUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Thumbnails/Screenshot%202026-03-14%20at%203.59.36%E2%80%AFPM.png?updatedAt=1773548963678",
    videoUrl:
      "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Production%20Testimonials/Poseidon%20Clean.mp4?updatedAt=1773548972184",
    content: "Any changes that we need to make to the website, they're super fast, super quick.",
  },
];

export const CUSTOMERS = [
  "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Customer%20Logos/1354.avif",
  "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Customer%20Logos/kjc-roofing.png?updatedAt=1773549334715",
  "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Customer%20Logos/ducky.png?updatedAt=1773549334734",
  "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Customer%20Logos/cape-cod.png?updatedAt=1773549334804",
  "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Customer%20Logos/pro-circuit-electric.webp?updatedAt=1773549334876",
  "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Customer%20Logos/high-angle-roofing.webp?updatedAt=1773549334839",
  "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Customer%20Logos/redrhino.png?updatedAt=1773549334757",
  "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Customer%20Logos/melissa-and-a-mop.png?updatedAt=1773549334735",
  "https://ik.imagekit.io/4wu305uo4/Public%20Pages%20Assets/Customer%20Logos/everspaugh-lawn-services.png?updatedAt=1773549334687",
];

export const FEATURES = [
  { name: "SEO-optimized", checked: ["rebolt", "agencies"] },
  { name: "Unlimited website changes", checked: ["rebolt"] },
  { name: "Affordable", checked: ["rebolt", "freelancers"] },
  { name: "Ready in hours", checked: ["rebolt"] },
  { name: "Pay up front", checked: ["agencies", "freelancers"] },
  { name: "Long-term contracts", checked: ["agencies"] },
];
